import {
  Directive,
  ElementRef,
  Renderer2,
  EventEmitter,
  Output,
  Attribute,
} from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { SubSink } from "subsink";
import { isDefined } from "@kells/utils/js";
import { FindingType, FindingsByTooth } from "@kells/interfaces/finding";
import {
  TREATMENT_PRIORITY_BY_TYPE,
  TreatmentTypes,
} from "@kells/clinic-one/apis";
import {
  getDisplayFindingType,
  getHexForFindingType,
  mixHexWithWhite,
} from "../../utils/report-finding-display.util";

/** CSS class applied when a tooth has at least one visible finding. */
const HAS_FINDING_CLASS = "tooth--has-finding";

@Directive({
  selector: "[tooth]",
  inputs: ["byTooth", "id"],
})
export class ToothDirective {
  @Output("click")
  click: EventEmitter<FindingsByTooth> = new EventEmitter<FindingsByTooth>();

  constructor(
    @Attribute("data-tooth") public name: string,
    public hostElement: ElementRef,
    private renderer: Renderer2
  ) {
    this.activateTooth = this.activateTooth.bind(this);
    this.handleClick = this.handleClick.bind(this);
  }

  isFilteredOut = false;

  /**
   * The finding type that should be used for tooth coloring.
   * When a filter is active, set this to the filter type so the tooth shows
   * that type's brand color (or greys out if the tooth lacks it).
   */
  selectedFilterType: FindingType | null = null;

  private _subs = new SubSink();
  toothId: string;

  private _findings: FindingsByTooth;

  public get findings(): FindingsByTooth {
    return this._findings!;
  }

  public set findings(value: FindingsByTooth) {
    this._findings = value;
    this.initStateFromFindings();
  }

  public makeInactive(): void {
    requestAnimationFrame(() => {
      this.removeSelectionClasses();
    });
  }

  /** Remove all finding-related classes and CSS vars, reset to default. */
  private clearFindingClasses(): void {
    const el = this.hostElement.nativeElement;
    el.classList.remove(HAS_FINDING_CLASS);
    // Keep legacy stage classes cleared for backward compat
    el.classList.remove("caries-initial-finding");
    el.classList.remove("caries-moderate-finding");
    el.classList.remove("caries-advanced-finding");
    el.classList.remove("default-no-finding");
    el.style.removeProperty("--tooth-brand");
    el.style.removeProperty("--tooth-fill");
    el.style.removeProperty("--tooth-fill-hover");
    el.style.removeProperty("--tooth-fill-selected");
  }

  public removeSelectionClasses(): void {
    this.clearFindingClasses();
    this.hostElement.nativeElement.classList.remove("selected");
    this.hostElement.nativeElement.classList.add("default-no-finding");
  }

  private _activeTooth$: BehaviorSubject<FindingsByTooth>;
  public get activeTooth(): BehaviorSubject<FindingsByTooth> {
    return this._activeTooth$;
  }

  public set activeTooth$(value$: BehaviorSubject<FindingsByTooth>) {
    this._activeTooth$ = value$;
    this._subs.sink = this._activeTooth$.subscribe((val: FindingsByTooth) => {
      if (val.tooth_id === this.findings.tooth_id) {
        this.hostElement.nativeElement.classList.add("selected");
      } else {
        this.hostElement.nativeElement.blur();
        this.hostElement.nativeElement.classList.remove("selected");
      }
    });
  }

  private _clickHandler: (() => void) | null = null;
  public set onClick(handler: (() => void) | null) {
    this._clickHandler = handler;
  }

  activateTooth() {
    if (
      isDefined(this._activeTooth$) &&
      isDefined(this.findings) &&
      !this.isFilteredOut
    ) {
      this._activeTooth$.next(this.findings);
    }
  }

  handleClick() {
    if (this.isFilteredOut) return;
    this.activateTooth();

    if (this._clickHandler) {
      this._clickHandler();
    }
  }

  get isAdvancedFindingType(): boolean {
    const advancedFindingTypes = [
      FindingType.Plaque,
      FindingType.GumRecession,
      FindingType.GumInflammation,
      FindingType.MissingTooth,
      FindingType.Fracture,
      FindingType.Calculus,
      FindingType.Infection,
      FindingType.DefectiveRestoration,
    ];

    return this.findings.allFindings.some((finding) =>
      advancedFindingTypes.includes(finding.type)
    );
  }

  get mostAdvancedTreatmentPriority(): number {
    let highestPriority = 0;
    this.findings.treatments.forEach((treatment) => {
      if (treatment.session !== this.findings.sessionId) {
        return;
      }
      const treatmentPriority: number =
        TREATMENT_PRIORITY_BY_TYPE[treatment.description as TreatmentTypes] ??
        0;

      highestPriority =
        highestPriority < treatmentPriority
          ? treatmentPriority
          : highestPriority;
    });
    return highestPriority;
  }

  /**
   * Apply the brand palette color for a given finding type onto the tooth
   * element via CSS custom properties consumed by mouth.component.scss.
   */
  private applyFindingTypeColor(type: FindingType): void {
    const brandHex = getHexForFindingType(type);
    const el = this.hostElement.nativeElement;
    el.style.setProperty("--tooth-brand", brandHex);
    // fill = very light tint; fill-hover = medium tint; fill-selected = dark tint
    el.style.setProperty("--tooth-fill", mixHexWithWhite(brandHex, 0.88));
    el.style.setProperty("--tooth-fill-hover", mixHexWithWhite(brandHex, 0.72));
    el.style.setProperty(
      "--tooth-fill-selected",
      mixHexWithWhite(brandHex, 0.18)
    );
    el.classList.add(HAS_FINDING_CLASS);
  }

  initStateFromFindings(): void {
    const { findings, hostElement } = this;

    this.clearFindingClasses();

    // Filtered-out teeth get the neutral default appearance — no brand color.
    if (this.isFilteredOut) {
      hostElement.nativeElement.classList.add("default-no-finding");
      this.renderer.removeAttribute(hostElement.nativeElement, "tabindex");
      return;
    }

    // Determine which finding type drives the tooth color.
    // When a filter is active, selectedFilterType is set by MouthComponent so
    // only teeth that match that type are colored with it.
    const displayType = getDisplayFindingType(
      findings,
      this.selectedFilterType
    );

    if (!displayType) {
      hostElement.nativeElement.classList.add("default-no-finding");
      this.renderer.removeAttribute(hostElement.nativeElement, "tabindex");
      return;
    }

    this.applyFindingTypeColor(displayType);
    hostElement.nativeElement.classList.remove("default-no-finding");
    this.renderer.setAttribute(hostElement.nativeElement, "tabindex", "0");
  }

  ngOnInit() {
    const idAttr = this.hostElement.nativeElement.getAttribute("id");
    if (idAttr) {
      this.toothId = idAttr.split("-")[1];
    }

    this.hostElement.nativeElement.addEventListener("click", this.handleClick);
  }
}
